import { useTranslation } from "react-i18next"
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material"
import { Link, navigate, useLocation, useParams } from "@cedarjs/router"
import { FindTankQuery2, FindTanksBySiteQuery, Tank as TankType } from "types/graphql"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { getReviewConfig } from "src/const/Defaults"
import { useEffect } from "react"


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      tabIndex={0}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}
interface TankProps {
  tank: FindTankQuery2["tanksBySite"][0]
}
const Tank = ({tank}: TankProps) => {
  const { idTank } = useParams()
  const location = useLocation();
  const reviewConfig = getReviewConfig()
  let buttonBackground;
  //Bassin en cours
  if(parseInt(idTank) == tank.id) 
    buttonBackground = "dark:bg-blue-800 bg-blue-300  dark:text-white/80 text-blue-700 !font-bold"
  else if(tank.tank_type.canHostBatch && !tank.lastBatchTransfer?.batch.id)
    buttonBackground = "dark:bg-black/30 bg-slate-100 dark:text-white/30 text-black/50"
  else 
    buttonBackground = "dark:bg-black/10 bg-slate-50 dark:text-white/80 text-black";
  
  let buttonBorder;
  if(tank?.lastReview?.state)
    buttonBorder += " border-0 box-border rounded-b-none border-b-2 border-solid " + reviewConfig[tank?.lastReview?.state]?.border

  return (
    <Button component={Link} to={`/${tank.idSite}/${tank.id}${location.pathname.match(/\/\d+\/\d+(\/.*)$/)?.[1] || ''}`} variant="text" size="small" className={`lg:px-3 md:px-2 min-w-fit  font-normal ${buttonBackground} ${buttonBorder}`}  sx={{ textTransform: 'none' }}>
      <Typography className="lg:text-base md:text-sm sm:text-xs" style={{fontWeight: 'inherit'}}>
        {tank.line || tank.shortName || tank.name.split(" ").pop()}
      </Typography>
      {/* <div className={`h-0.5 w-5 shadow-md relative z-20 ${tank.status}`} /> */}
    </Button>
  )
}

const Tanks = ({tanks, title}) => {
  return (
    <div className="flex lg:mx-3 md:mx-2 flex-col items-center align-middle">
      <Typography variant="caption" className="whitespace-nowrap">{title}</Typography>
      <div className="flex flex-row space-x-0.5">
        {tanks.sort((a:TankType,b:TankType) => a.name.localeCompare(b.name)).map((tank, index) => <Tank tank={tank} key={index}/>)}
      </div>
    </div>
  )
}

const TankMenu = (props: {tanks: FindTanksBySiteQuery["tanksBySite"], batchTanks: FindTanksBySiteQuery["tanksBySite"], technicalTanks: FindTanksBySiteQuery["tanksBySite"]}) => {
  const { t } = useTranslation()
  const location = useLocation();
  const { idTank } = useParams()
  const currentIndex = props.tanks.findIndex(tank => tank.id == parseInt(idTank))
  const previousTank = currentIndex >= 1 ? props.tanks[currentIndex-1] : null
  const nextTank = (currentIndex != -1) && (currentIndex < props.tanks.length -1) ? props.tanks[currentIndex+1] : null
  const handleChange = (event) => {
    const _tank = props.tanks.find(tank => tank.id == event.target.value)
    return navigateWithLocation(_tank, location)
  }
  const navigateWithLocation = (_tank, _location) => {
    navigate(`/${_tank.idSite}/${_tank.id}${_location.pathname.match(/\/\d+\/\d+(\/.*)$/)?.[1] || ''}`)
  }
  
  //Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      //Don't trigger navigation if user's on an input
      //@ts-expect-error type does exists in target
      if(["textarea", "input", "text"].indexOf(event.target.type) >= 0)
        return;

      const _currentIndex = props.tanks.findIndex(tank => tank.id == parseInt(idTank))
      if (event.key === 'ArrowLeft' && _currentIndex >= 1) 
        navigateWithLocation(props.tanks[currentIndex-1], location);
      else if (event.key === 'ArrowRight' && (_currentIndex != -1) && (_currentIndex < props.tanks.length -1)) 
        navigateWithLocation(props.tanks[currentIndex+1], location);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTank,location]);


  //Tab presentation
  const [value, setValue] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <div className="flex-col flex justify-center md:w-fit w-full">
      <Box className="md:block hidden " sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleTabChange} centered sx={{ minHeight: '34px', height: '34px'}} >
          <Tab label={t("titles.batchTanks")}  sx={{ minHeight: '34px', height: '34px', fontSize: "13px"}} />
          <Tab label={t("titles.technicalTanks")}  sx={{ minHeight: '34px', height: '34px', fontSize: "13px"}} />
        </Tabs>
      </Box>

      <CustomTabPanel value={value} index={0}>
        <div className="md:flex hidden flex-row justify-around overflow-x-hidden">
          {props.batchTanks.map((tanks, index) => <Tanks tanks={tanks} title={t(`dynamic.tankType.${tanks[0].tank_type.name}`, {defaultValue: tanks[0].tank_type.name})} key={index} />)}
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <div className="md:flex hidden flex-row justify-around overflow-x-hidden">
          {props.technicalTanks.map((tanks, index) => <Tanks tanks={tanks} title={t(`dynamic.tankType.${tanks[0].tank_type.name}`, {defaultValue: tanks[0].tank_type.name})} key={index} />)}
        </div>
      </CustomTabPanel>
      <div className="md:hidden pt-2 grid grid-cols-3 w-full items-center justify-between">
        {previousTank && 
          <Button variant="text" onClick={() => handleChange({target: {value:previousTank.id}})} startIcon={<FontAwesomeIcon icon={faChevronLeft}/>}>{previousTank?.shortName || previousTank?.name}</Button>
        }
        <FormControl className="w-full col-start-2" size="small" key={idTank}>
          <InputLabel id="tank">{t("labels.tank")}</InputLabel>
          <Select label={t("labels.tank")}
            className="w-full"
            onChange={handleChange}
            // {...(defaultValue ? {defaultValue} : {})}
            defaultValue={idTank || ""}
            MenuProps={{
              slotProps: {
                paper: {
                  style: {
                    maxHeight: 400,
                  }
                }
              }
            }}>
            {props.tanks.map(tank =>
              <MenuItem key={tank.id} value={tank.id}>{tank.name}</MenuItem>
            )}
          </Select>
        </FormControl>
        {nextTank && 
        <Button variant="text" onClick={() => handleChange({target: {value:nextTank.id}})} endIcon={<FontAwesomeIcon icon={faChevronRight}/>}>{nextTank?.shortName || nextTank?.name}</Button>
        }
      </div>
    </div>
  )
}

export default TankMenu
